import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { autoinject, bind, inject, Modal, ModalHandle, Tooltip, TasksStore, deepStringFind } from 'Mri.Web.Common';
import * as React from 'react';
import {Button, DropdownItem, DropdownMenu, DropdownToggle, Input, UncontrolledButtonDropdown} from 'reactstrap';
import { BsSlashCircle } from '@react-icons/all-files/bs/BsSlashCircle';
import { BsClipboard } from '@react-icons/all-files/bs/BsClipboard';
import { BsFileEarmarkArrowDown } from '@react-icons/all-files/bs/BsFileEarmarkArrowDown';
import {DisplayListViewModel} from "../../DisplayFilters/DisplayListViewModel";
import {DisplayGridViewTypes} from "./DisplayGridViewSelector";
import {DisplaysStore} from "../DisplaysStore";
import {AppStore} from "../../Core/AppStore";
import {
    BsBootstrapReboot, BsBroadcastPin,
    BsCheckSquare,
    BsClipboardCheck,
    BsFileExcel,
    BsFiletypeCsv, BsPower, BsRewind,
    BsSendExclamation,
    BsSquare
} from "react-icons/bs";
import {BsDashCircle} from "@react-icons/all-files/bs/BsDashCircle";

export interface IGridBulkActionsProps {
    readonly vm: DisplayListViewModel;
    readonly className?: string;
    readonly size?: 'md' | 'sm' | 'lg';
    readonly link?: boolean;
}

@autoinject
@observer
export class GridBulkActions extends React.Component<IGridBulkActionsProps> {
    public render() {
        const { gridViewType, total } = this.props.vm;
        return (
            <div>
                <div className="d-inline-flex">
                    <UncontrolledButtonDropdown>
                        <DropdownToggle
                            caret
                            size={this.props.size ?? 'sm'}
                            color={this.props.link ? 'link' : undefined}
                        >
                            <BsClipboardCheck className="f-20 me-1"/> Selected Actions
                        </DropdownToggle>

                        <DropdownMenu className="p-0 dropdown-padding">
                            <DropdownItem tag="a" className="text-header fw-bold py-0" onClick={this.kickWebApiOnDisplays}>
                                <BsBroadcastPin className="f-24 me-2 pb-2"/> WebApi Kick Selected Displays
                            </DropdownItem>
                            <DropdownItem tag="a" className="text-header fw-bold py-0" onClick={this.rebootDisplays}>
                                <BsBootstrapReboot className="f-24 me-2 pb-1"/> Reboot Selected Displays
                            </DropdownItem>
                            <DropdownItem tag="a" className="text-header fw-bold py-0" onClick={this.restartServerOnDisplays}>
                                <BsRewind className="f-24 me-2 pb-1"/> Restart Server On Selected Displays
                            </DropdownItem>
                            <DropdownItem tag="a" className="text-header fw-bold py-0" onClick={this.enableStandbyModeOnDisplays}>
                                <BsPower className="f-24 me-2 pb-1"/> Enable Standby Mode On Selected Displays
                            </DropdownItem>
                            <DropdownItem tag="a" className="text-header fw-bold py-0" onClick={this.disableStandbyModeOnDisplays}>
                                <BsDashCircle className="f-24 me-2 pb-1"/> Disable Standby Mode On Selected Displays
                            </DropdownItem>
                        </DropdownMenu>
                    </UncontrolledButtonDropdown>

                    <UncontrolledButtonDropdown className="ms-1">
                        <DropdownToggle
                            caret
                            size={this.props.size ?? 'sm'}
                            color={this.props.link ? 'link' : undefined}
                        >
                            <BsClipboard className="f-20 me-1"/> Filter Actions
                        </DropdownToggle>

                        <DropdownMenu className="p-0 dropdown-padding">
                            {gridViewType == DisplayGridViewTypes.Grid  && (
                                <>
                                    <DropdownItem
                                        tag="a"
                                        className="text-header fw-bold py-0"
                                        onClick={this.exportToCSV}
                                    >
                                        <BsFiletypeCsv /> CSV Export
                                    </DropdownItem>

                                    <DropdownItem
                                        tag="a"
                                        className="text-header fw-bold py-0"
                                        onClick={this.exportToExcel}
                                    >
                                        <BsFileExcel /> Excel Export
                                    </DropdownItem>
                                </>
                            )}

                            <DropdownItem tag="a" className="text-header fw-bold py-0" onClick={this.ignoreAll}>
                                <BsSlashCircle /> Ignore All Events
                            </DropdownItem>

                            {gridViewType == DisplayGridViewTypes.MapView && (
                                <DropdownItem
                                    tag="a"
                                    className="text-header fw-bold py-0"
                                    onClick={this.exportLocations}
                                >
                                    <BsFileEarmarkArrowDown /> Export Locations
                                </DropdownItem>
                            )}
                        </DropdownMenu>
                    </UncontrolledButtonDropdown>
                </div>
    
                <Modal
                    className="modal-md"
                    title="Confirm Action"
                    modalHandle={this.confirmModalHandle}
                    btnClass="w-50"
                    footerClass="d-flex justify-content-center"
                >
                    
                    Are you sure you want to apply this action on {this.serialNumbers.length > 0 ?
                    <strong>{this.serialNumbers.length}</strong> : <strong>{total}</strong>} display(s)?
                </Modal>
            </div>
        );
    }
    
    @bind
    @action
    private async ignoreAll() {
        const { displayFilter } = this.props.vm;
        if (await this.confirmModalHandle.confirm()) await this.displaysStore.ignoreDisplayEvents(displayFilter);
    }

    @bind
    private async exportLocations() {
        const task = this.tasksStore.startSessionTask('Export CSV');
        const { displayFilter } = this.props.vm;

        try {
            await this.displaysStore.exportLocationDataAsync(displayFilter);
            task.complete();
        } catch (e: any) {
            task.error(e);
        }
    }

    @bind
    private  exportToCSV() {
        this.appStore.gridRef?.api.exportDataAsCsv();
    }

    @bind
    private async exportToExcel() {
        this.appStore.gridRef?.api.exportDataAsExcel();
    }

    @bind
    @action
    private async kickWebApiOnDisplays() {
        this.appStore.gridRef?.api.selectAll();
        let rows = this.appStore.gridRef?.api.getSelectedRows();
        this.serialNumbers = rows?.map((row) => row.serialNumber) || [];
        if(this.serialNumbers.length > 0) {
            if (await this.confirmModalHandle.confirm()){
                const task = this.tasksStore.startSessionTask('WebApi Kick Display(s)');

                try {
                    await this.displaysStore.kickWebApiForDisplays(this.serialNumbers);
                    task.complete();
                } catch (e: any) {
                    task.error(e);
                }
            }
        }
    }

    @bind
    @action
    private async rebootDisplays() {
        this.appStore.gridRef?.api.selectAll();
        let rows = this.appStore.gridRef?.api.getSelectedRows();
        this.serialNumbers = rows?.map((row) => row.serialNumber) || [];
        if(this.serialNumbers.length > 0) {
            if (await this.confirmModalHandle.confirm()){
                const task = this.tasksStore.startSessionTask('Reboot Display(s)');

                try {
                    await this.displaysStore.rebootDisplays(this.serialNumbers);
                    task.complete();
                } catch (e: any) {
                    task.error(e);
                }
            }
        }
    }

    @bind
    @action
    private async enableStandbyModeOnDisplays() {
        this.appStore.gridRef?.api.selectAll();
        let rows = this.appStore.gridRef?.api.getSelectedRows();
        this.serialNumbers = rows?.map((row) => row.serialNumber) || [];
        if(this.serialNumbers.length > 0) {
            if (await this.confirmModalHandle.confirm()){
                const task = this.tasksStore.startSessionTask('Enable Standby Mode For Display(s)');

                try {
                    await this.displaysStore.enableStandbyModeForDisplays(this.serialNumbers);
                    task.complete();
                } catch (e: any) {
                    task.error(e);
                }
            }
        }
    }
    
    @bind
    @action
    private async disableStandbyModeOnDisplays() {
        this.appStore.gridRef?.api.selectAll();
        let rows = this.appStore.gridRef?.api.getSelectedRows();
        this.serialNumbers = rows?.map((row) => row.serialNumber) || [];
        if(this.serialNumbers.length > 0) {
            if (await this.confirmModalHandle.confirm()){
                const task = this.tasksStore.startSessionTask('Disable Standby Mode For Display(s)');

                try {
                    await this.displaysStore.disableStandbyModeForDisplays(this.serialNumbers);
                    task.complete();
                } catch (e: any) {
                    task.error(e);
                }
            }
        }
    }

    @bind
    @action
    private async restartServerOnDisplays() {
        this.appStore.gridRef?.api.selectAll();
        let rows = this.appStore.gridRef?.api.getSelectedRows();
        this.serialNumbers = rows?.map((row) => row.serialNumber) || [];
        if(this.serialNumbers.length > 0) {
            if (await this.confirmModalHandle.confirm()){
                const task = this.tasksStore.startSessionTask('Restart Server For Display(s)');

                try {
                    await this.displaysStore.restartServerForDisplays(this.serialNumbers);
                    task.complete();
                } catch (e: any) {
                    task.error(e);
                }
            }
        }
    }
    
    @inject
    private displaysStore!: DisplaysStore;
    @inject
    private tasksStore!: TasksStore;
    @inject
    private appStore!: AppStore;
    @observable
    private serialNumbers: string[] = [];
    private confirmModalHandle = new ModalHandle();
}
